<template>
    <div id="users-block" class="flex flex-column h-full">
        <!-- Header -->
        <div class="w-full flex flex-row justify-content-between align-items-center" style="height: 3.95rem; padding: 1rem; background-color: var(--users-block-header-background-color); border-top-left-radius: var(--my-border-radius); border-top-right-radius: var(--my-border-radius);">
            <span style="font-size: var(--users-title-font-size); font-weight: var(--users-title-font-weight); padding: 0px; margin: 0px; margin-bottom: 0;" @click="showNewVersion = !showNewVersion">Users</span>
            <Button outlined :disabled="selectedUser===null" style="padding: 0.3rem 0.6rem; min-width: 8rem;" @click="filterOnCopy = !filterOnCopy">
                <div class="flex flex-row w-full h-full align-items-center justify-content-center">
                    <div style="flex: 1;" class="flex flex-row align-items-center justify-content-center">
                        <SvgFilterOn v-if="filterOnCopy" className="button-svg" color="var(--button-normal-color)"/>
                        <SvgFilterOff v-else className="button-svg" color="var(--button-normal-color)"/>
                    </div>
                    <span style="flex: 2" class="flex flex-row w-full h-full align-items-center justify-content-start">Filter: {{ filterOnCopy ? 'On' : 'Off' }}</span>
                </div>
            </Button>
        </div>

        <!-- Content -->
        <div class="w-full flex flex-column justify-content-between h-full" style="padding: 0.3rem; padding-bottom: 0; background-color: var(--users-block-content-background-color); border-bottom-left-radius: var(--my-border-radius); border-bottom-right-radius: var(--my-border-radius);">
            <!-- Users scrolling panel -->
            <div class="users w-full flex-1 relative" style="margin-top: 0px;">
                <ScrollPanel style="padding: 0px;" class="absolute h-full w-full">
                    <div>
                        <UsersList :selectedUser="selectedUser" @clicked-user="(val) => $emit('clicked-user', val)" @delete="(val) => deleteUser(val)"/>
                    </div>
                </ScrollPanel>
            </div>

            <!-- Buttons  -->
            <div class="users-button flex-end flex flex-row justify-content-between gap-2" style="padding: 1rem;">
                <Button outlined style="flex: 5;" size="small" @click="showAddNewUserDialog = true">
                    <div class="flex flex-row w-full justify-content-center align-items-center gap-2">
                        <SvgAddUser className="table-licenses-svg" color="var(--button-normal-color)"/>
                        <span>Add a New User</span>
                    </div>
                </Button>
            </div>
        </div>
    </div>

    <!-- DIALOGS -->

    <!-- Add a New User window -->
    <AddNewUserDialog :showDialog="showAddNewUserDialog" @close="showAddNewUserDialog = false"/>

    <!-- Delete a User window -->
    <ConfirmationDeleteUserDialog :showDialog="showConfirmationDeleteUserDialog" :userToDelete="userToDelete" @close="showConfirmationDeleteUserDialog = false"/>
</template>

<script setup>
// --- IMPORTS ---
import { ref, toRefs, watchEffect, computed } from 'vue';
// PrimeVue
import Button from 'primevue/button';
import ScrollPanel from 'primevue/scrollpanel'
// vue
import UsersList from './Elements/UsersList.vue'
import AddNewUserDialog from '@/components/Dialogs/Actions/AddNewUserDialog.vue'
import ConfirmationDeleteUserDialog from '@/components/Dialogs/Actions/ConfirmationDeleteUserDialog.vue'
import SvgAddUser from '@/components/SVGs/SvgAddUser.vue'
import SvgFilterOn from '@/components/SVGs/SvgFilterOn.vue'
import SvgFilterOff from '@/components/SVGs/SvgFilterOff.vue'
// Others
import toastService from '@/composables/toastService';
import { useStore } from 'vuex';
import UsersUtils from '@/composables/UsersUtils';

const store = useStore()
const { myUsers, myOwners } = UsersUtils(store)

// --- PROPS ---
const props = defineProps({
    selectedUser: Object,
    filterOn: Boolean
})
const { selectedUser } = toRefs(props)

// --- User info ---
const user = computed(() => store.state.user.user)

// --- EMITS ---
const emit = defineEmits(['clicked-user', 'user-filter-changed', 'update:filter-on'])

// --- Filtering On/Off ---
const filterOnCopy = ref(props.filterOn)
watchEffect(() => {
    if (selectedUser.value === null) { filterOnCopy.value = false }
})
watchEffect(() => {
    emit('update:filter-on', filterOnCopy.value)
})

// --- My Users / Other Users ---
const showNewVersion = ref(false) // for demonstration purpose

// --- Add a New User ---
const showAddNewUserDialog = ref(false)

// --- Delete a User ---
const showConfirmationDeleteUserDialog = ref(false)
const userToDelete = ref(null)
const deleteUser = (correspondingUser) => {
    // Check if user is in the "My Users" list
    if (!myUsers.value.some((u) => u.id === correspondingUser.id)) {
        toastService.add({ severity: 'error', summary: "Deletion Error", detail: 'You can only delete one of your users.', life: 5000 });
        return
    }
    // Check if user is NOT in the "My Owners" list
    if (myOwners.value.some((u) => u.id === correspondingUser.id)) {
        toastService.add({ severity: 'error', summary: "Deletion Error", detail: 'You can not delete an owner.', life: 5000 });
        return
    }
    // Check if I can delete the user (i.e. the user is not currently using one of my licenses)
    let n = store.state.licenses.licenses.filter((license) => (license.admin.id === user.value.id) && (license.current_status.activated) && license.current_status.activeUser && ((license.current_status.activeUser.id ?? "-1") === correspondingUser.id)).length
    if (n>0) {
        toastService.add({ severity: 'error', summary: "Deletion Error", detail: `The user you want to delete is currently using ${n} license${n>1 ? 's' : ''} from you. Make sure you stop the running license${n>1 ? 's' : ''} before trying to delete the user.`, life: 5000 });
        return
    }
    // Confirmation message
    userToDelete.value = correspondingUser
    showConfirmationDeleteUserDialog.value = true
}
</script>

<style scoped>
#users-block {
    color: var(--theme-font-color);
}
</style>